import React, { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, dashboardPathForRole } from '../../context/AuthContext';
import { apiPost } from '../../services/api';

export default function VerifyEmailPending() {
  const { user, authLoading, isVerified, token } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const MAX_RESENDS = 5;

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (authLoading) return;
    // If a token exists but user hasn't loaded yet, wait for hydration
    if (!user && token) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (isVerified) {
      navigate(dashboardPathForRole(user.role), { replace: true });
    }
  }, [user, authLoading, isVerified, navigate, token]);

  async function handleResend() {
    setError('');
    setResendSuccess(false);
    setSubmitting(true);
    try {
      const res = await apiPost('/api/v1/auth/email/resend', {}, token);
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
        setCooldown(60);
        setResendCount((c) => c + 1);
        return;
      }
      if (!res.ok) {
        setError(data.message || 'Something went wrong. Please try again.');
        return;
      }
      setResendSuccess(true);
      setCooldown(60);
      setResendCount((c) => c + 1);
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading || (!user && token) || !user || isVerified) {
    return (
      <div className="rwm-auth bg-white">
        <div className="rwm-auth__card py-5 text-center text-muted">
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rwm-auth bg-white" data-testid="rwm-verify-pending">
      <div className="rwm-auth__card py-5">
        <Card className="border" style={{ borderColor: 'var(--rwm-border)' }}>
          <Card.Body className="p-4">
            <h1 className="h4 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
              Check your email
            </h1>
            <p className="text-muted mb-4">
              We&apos;ve sent a verification link to <strong>{user.email}</strong>. Click the link to activate your
              account.
            </p>
            {resendSuccess ? (
              <p className="small text-success mb-3">Email sent! Check your inbox.</p>
            ) : null}
            {error ? <p className="small text-danger mb-3">{error}</p> : null}
            {cooldown > 0 && resendCount < MAX_RESENDS ? (
              <p className="small text-muted mb-2" data-testid="rwm-resend-cooldown">
                Resend again in {cooldown}s
              </p>
            ) : null}
            {resendCount >= MAX_RESENDS ? (
              <p className="small text-muted mb-3" data-testid="rwm-resend-max">
                Maximum resend attempts reached. Please contact support if you need help.
              </p>
            ) : (
              <Button
                type="button"
                variant="info"
                className="text-white"
                disabled={submitting || cooldown > 0 || resendCount >= MAX_RESENDS}
                onClick={handleResend}
                data-testid="rwm-resend-verification"
              >
                {submitting
                  ? 'Sending...'
                  : cooldown > 0
                    ? `Resend again in ${cooldown}s`
                    : 'Resend verification email'}
              </Button>
            )}
            <p className="small text-muted mt-3 mb-0">
              <Link to="/login" className="rwm-job-detail__browse-link text-decoration-none">
                Back to Log In
              </Link>
            </p>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
